import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import Address from "./Address";
import DocumentRef from "./DocumentRef";
import SalesLeadStatus from "./SalesLeadStatus";
import LeadItem from "./LeadItem";
import { deepFreeze, timestampToDate } from "../util/misc";
import { cloneDeep, every, get, isEmpty, omit, sortBy } from "lodash";
import { diff } from "deep-object-diff";

export const SALES_LEAD_FIELDS = {
  name: "",
  receivedFrom: null,
  receivedDate: null,
  dueDate: null,
  type: "",
  address: {
    development: "",
    lot: "",
    street: "",
    city: "",
    state: "",
    zip: "",
  },
  plans: [],
  items: [],
  notes: "",
  status: { type: "open" },
  notax: false,
  tax: null,
  terms: null,
  saleID: "",
  projectID: "",
  isSold: false,
  locked: false,
  archived: false,

  // Service issue linkage - for quotes created from service issues
  serviceIssueID: "",

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(SALES_LEAD_FIELDS);

export const SEARCHABLE_FIELDS = [
  "id",
  "data.name",
  "data.address.street",
  "data.address.lot",
  "data.address.development",
  "data.address.city",
  "data.address.state",
  "data.address.zip",
  "data.notes",
  "receivedFrom.name",
  "status.assignedTo.name",
];
deepFreeze(SEARCHABLE_FIELDS);

// Fields that are owned by cloud functions (_createSale / _createProject /
// _changeSale) and must never be written by the client-side lead editor. A
// stale edit buffer once overwrote these with their defaults, severing a sold
// lead's link to the sale it created. The lead-save path and the edit dialog's
// draft restore both use this list to preserve the authoritative server values.
export const SALES_LEAD_SYSTEM_FIELDS = [
  "isSold",
  "saleID",
  "projectID",
  "created",
  "createdBy",
];
deepFreeze(SALES_LEAD_SYSTEM_FIELDS);

export const SALES_LEAD_TYPES = {
  new: {
    label: "Residential: New Construction",
    icon: "new_home",
    projectType: "residential",
  },
  remodel: {
    label: "Residential: Remodel/Replace",
    icon: "remodel",
    projectType: "residential",
  },
  historic: {
    label: "Commercial: Historic",
    icon: "historic",
    projectType: "commercial",
  },
  multifamily: {
    label: "Commercial: Multi-Family",
    icon: "multifamily",
    projectType: "commercial",
  },
  office: {
    label: "Commercial: Office/Retail",
    icon: "office",
    projectType: "commercial",
  },
};
deepFreeze(SALES_LEAD_TYPES);

export default class SalesLead extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, SALES_LEAD_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      create: SalesLead.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
      unlock: this.canBeUnlockedBy,
      "create-quote": this.canCreateQuote,
    });

    // Documents
    this.data.plans = this.plans.map((d) => d.data);

    // Status
    this.data.status = this.status.data;
  }

  // Permissions

  static canBeCreatedBy(user) {
    return user.isEmployee;
  }

  canBeEditedBy(user) {
    return (
      this.isEmpty || user.isAdmin || (user.isEmployee && !this.data.locked)
    );
  }

  canBeDeletedBy(user) {
    return !this.data.locked && user.isAdmin;
  }

  canBeUnlockedBy(user) {
    return !this.data.isSold && user.isAdmin;
  }

  canCreateQuote(user) {
    return this.id && !this.data.locked && user.isEmployee;
  }

  // Database

  get path() {
    return SalesLead.getPath(this.id);
  }

  static getPath(id) {
    let path = `sales-leads`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(salesLead) {
    return salesLead.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new SalesLead(snapshot.id, data);
  }

  // Editing

  get rules() {
    return {
      type: [(a) => Boolean(a) || "required"],
      receivedFrom: [(a) => Boolean(a) || "required"],
      receivedDate: [(a) => Boolean(a) || "required"],
      dueDate: [(a) => Boolean(a) || "required"],
    };
  }

  get isValid() {
    return Boolean(
      every(this.rules, (rules, key) =>
        rules.every((rule) => rule(get(this.data, key)) === true)
      ) &&
        this.status &&
        this.status.isValid
    );
  }

  get isEmpty() {
    return (
      !this.id &&
      isEmpty(
        diff(
          omit(SalesLead.sanitize(this.data), "receivedDate"),
          omit(new SalesLead().data, "receivedDate")
        )
      )
    );
  }

  isEqual(salesLead) {
    return (
      this.id === salesLead.id &&
      isEmpty(
        diff(SalesLead.sanitize(this.data), SalesLead.sanitize(salesLead.data))
      )
    );
  }

  static cleanDocuments(data, removeUnsaved = false) {
    data.plans = data.plans.filter(
      (d) => new DocumentRef(d)[removeUnsaved ? "isSaved" : "isValid"]
    );
    SalesLeadStatus.cleanDocuments(data.status, removeUnsaved);
    return data;
  }

  static sanitize(data) {
    data = cloneDeep(data);

    SalesLead.cleanDocuments(data);

    // Remove empty items
    data.items = data.items.filter((i) => new LeadItem(i).isValid);

    data.status = SalesLeadStatus.sanitize(data.status);

    return data;
  }

  get finalQuotes() {
    return this.quotes.filter((quote) => {
      const response = quote.status.response;
      return !response || response.isApproved;
    });
  }

  // Formatting

  get quotes() {
    return sortBy(
      Object.values(store.state.main.quotes).filter(
        (quote) => quote.salesLeadID === this.id
      ),
      "created"
    );
  }

  get type() {
    return SALES_LEAD_TYPES[this.data.type] || { label: "", icon: "" };
  }

  get status() {
    return new SalesLeadStatus(this.data.status);
  }

  get project() {
    return this.data.projectID
      ? store.state.main.projects[this.data.projectID] || null
      : null;
  }

  get sale() {
    return this.data.projectID && this.data.saleID
      ? store.state.main.sales[this.data.projectID + this.data.saleID]
      : null;
  }

  // The jobsite is this lead's address, or the project's once sold — the
  // project address may have been corrected after the sale.
  get jobsite() {
    const project = this.project;
    if (project && project.address && project.address.isValid) {
      return project.address;
    }
    return this.address;
  }

  get receivedFrom() {
    return this.data.receivedFrom
      ? store.state.main.contacts[this.data.receivedFrom] || null
      : null;
  }

  // Location value(s) used for location-based filtering. Leads have no explicit
  // location, so this derives from the assigned user. Open (unassigned) leads
  // return an empty array and are always shown regardless of location filter.
  get locationValues() {
    const assignedID = this.data.status && this.data.status.assignedTo;
    if (!assignedID) {
      return [];
    }
    const user = store.state.main.users[assignedID];
    if (!user || !Array.isArray(user.data.locations)) {
      return [];
    }
    return [...new Set(user.data.locations.filter(Boolean))];
  }

  get address() {
    return new Address(this.data.address);
  }

  get items() {
    return this.data.items.map((i) => new LeadItem(i));
  }

  get plans() {
    return this.data.plans.map((d) => new DocumentRef(d));
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy] || null
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy] || null
      : null;
  }

  toString(options) {
    return this.address.toString(options);
  }
}
